import React, { useState, useEffect } from "react";
import { Table, Card, Spinner, Form, Row, Col, Button } from "react-bootstrap";
import { fetchAPI } from "../utils/fetchAPI";
import { Device } from "./DeviceCard";
import { getCountBadge } from "../utils/getCountBadge";
import { useNavigate } from "react-router-dom";
import { WhiteBoxWrapper } from "./Login.style";
import * as XLSX from "xlsx";
import { FaFileExcel } from "react-icons/fa";

const StockList = () => {
  const navigate = useNavigate();
  const [devices, setDevices] = useState<Device[]>([]);
  const [filteredDevices, setFilteredDevices] = useState<Device[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [selectedType, setSelectedType] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>("");

  // Pobieranie unikalnych typów urządzeń
  const uniqueTypes = Array.from(new Set(devices.map((device) => device.type).filter(Boolean)));

  useEffect(() => {
    const fetchDevices = async () => {
      setIsLoading(true);
      setErrorMessage("");
      try {
        const data = await fetchAPI("GET", "devices.php?unassigned=1");
        if (data.success) {
          // Filtrujemy urządzenia, które nie są przypisane do żadnego użytkownika (nie występują w tabeli current)
          // lub mają zerowy stan
          const filteredDevices = data.data.filter((device: Device) => !device.assignedTo || device.count === 0);
          setDevices(filteredDevices || []);
          setFilteredDevices(filteredDevices || []);
        } else {
          throw new Error(data.message || "Nie udało się pobrać urządzeń.");
        }
      } catch (error: any) {
        setErrorMessage(error.message || "Wystąpił błąd podczas pobierania danych.");
      } finally {
        setIsLoading(false);
      }
    };

    fetchDevices();
  }, []);

  useEffect(() => {
    // Filtrowanie urządzeń na podstawie wyszukiwanej nazwy i wybranego typu
    const filtered = devices.filter((device) => {
      const matchesSearch = device.name.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesType = !selectedType || device.type === selectedType;
      return matchesSearch && matchesType;
    });
    setFilteredDevices(filtered);
  }, [searchTerm, selectedType, devices]);

  const generateExcel = () => {
    // Przygotuj dane do eksportu
    const exportData = filteredDevices.map((device) => ({
      Nazwa: device.name,
      Typ: device.type || "-",
      Lokalizacja: device.category || "-",
      "Liczba sztuk w magazynie": device.returnable === 1 ? "1" : device.count || "0",
    }));

    // Utwórz nowy arkusz
    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Stany Magazynowe");

    // Zapisz plik
    XLSX.writeFile(wb, "stany_magazynowe.xlsx");
  };

  return (
    <Card className="p-3 mt-4">
      <WhiteBoxWrapper>
        <h2 className="text-center">
          <b>Aktualne stany magazynowe</b>
        </h2>
        <Row className="mt-3">
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Control
                type="text"
                placeholder="Wyszukaj po nazwie..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Select value={selectedType} onChange={(e) => setSelectedType(e.target.value)}>
                <option value="">Wszystkie typy</option>
                {uniqueTypes.map((type) => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>
        </Row>
        <Row>
          <Col xs={12} className="d-flex justify-content-end mt-3">
            <Button variant="primary" onClick={generateExcel}>
              <FaFileExcel className="me-2" /> Generuj Excel
            </Button>
          </Col>
        </Row>
      </WhiteBoxWrapper>
      {isLoading ? (
        <Spinner animation="border" />
      ) : errorMessage ? (
        <p className="text-danger">{errorMessage}</p>
      ) : (
        <Table striped bordered hover responsive>
          <thead>
            <tr>
              <th>Nazwa</th>
              <th>Typ</th>
              <th>Lokalizacja</th>
              <th>Liczba sztuk w magazynie</th>
            </tr>
          </thead>
          <tbody>
            {filteredDevices.length > 0 ? (
              filteredDevices.map((device) => (
                <tr key={device.id}>
                  <td>
                    <span onClick={() => navigate(`/device/${device.id}`)}>
                      <b>{device.name}</b>
                    </span>
                  </td>
                  <td>{device.type || "-"}</td>
                  <td>{device.category || "-"}</td>
                  <td>
                    {device.returnable === 1 ? (
                      <span className="badge bg-success">1</span>
                    ) : (
                      getCountBadge(device.count)
                    )}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={4} className="text-center">
                  {searchTerm || selectedType
                    ? "Nie znaleziono urządzeń o podanych kryteriach."
                    : "Brak urządzeń w magazynie."}
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      )}
    </Card>
  );
};

export default StockList;
